import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { TenantSpoofService } from '../services/tenant-spoof.service';

/**
 * Known base hostnames where the first subdomain segment is NOT a tenant.
 * e.g. "pdtf-pipeline-dev.collaborativeconveyancing.com" → no tenant
 *      "dutton-gregory.pdtf-pipeline-dev.collaborativeconveyancing.com" → tenant "dutton-gregory"
 *
 * The trailing `'local'` entry powers the local-dev workflow:
 *      "dutton-gregory.local:4200"  → tenant "dutton-gregory"
 *      "bettr-legal.local:4200"     → tenant "bettr-legal"
 * To use it, add `127.0.0.1 dutton-gregory.local` to your hosts file
 * (`C:\Windows\System32\drivers\etc\hosts` on Windows) and start the dev
 * server with `ng serve --host 0.0.0.0 --disable-host-check` so it accepts
 * the non-localhost hostname.
 */
const BASE_HOSTNAMES = [
  'pdtf-pipeline-dev.collaborativeconveyancing.com',
  'pdtf-pipeline-uat.collaborativeconveyancing.com',
  'pdtf-pipeline.collaborativeconveyancing.com',
  'local',
];

const TENANT_SLUG = /^[a-z0-9][a-z0-9-]*$/i;

/** Extract tenant name from hostname. Exported for testability. */
export function extractTenant(hostname: string): string | null {
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  for (const base of BASE_HOSTNAMES) {
    if (hostname === base) {
      return null;
    }
    if (hostname.endsWith('.' + base)) {
      const tenant = hostname.slice(0, hostname.length - base.length - 1);
      return TENANT_SLUG.test(tenant) ? tenant : null;
    }
  }
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const tenant = parts[0];
    return TENANT_SLUG.test(tenant) ? tenant : null;
  }
  return null;
}

/**
 * Attach `__tenant: <slug>` to outbound backend API calls. The tenant is
 * derived from the SPA's current hostname (e.g. `dutton-gregory.pdtf-pipeline-
 * dev.collaborativeconveyancing.com` → `dutton-gregory`). On localhost or the
 * unscoped base domain no header is sent — the backend falls back to the
 * `tenant_id` claim on the JWT.
 *
 * For QA/manual testing, TenantSpoofService.spoofTenant(name) lets you
 * override the value without changing subdomain.
 */
@Injectable()
export class TenantInterceptor implements HttpInterceptor {
  private readonly httpBase = environment.pipeline?.httpBase ?? '';
  private readonly apiBase = environment.apis?.default?.url ?? '';

  constructor(private spoof: TenantSpoofService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isBackendRequest(req.url)) {
      return next.handle(req);
    }
    const tenant = this.spoof.spoofedTenant ?? extractTenant(window.location.hostname);
    if (!tenant) {
      return next.handle(req);
    }
    return next.handle(req.clone({ setHeaders: { __tenant: tenant } }));
  }

  private isBackendRequest(url: string): boolean {
    if (!url.startsWith('http')) {
      // relative URLs always hit our own backend
      return true;
    }
    if (this.httpBase && url.startsWith(this.httpBase)) return true;
    if (this.apiBase && url.startsWith(this.apiBase)) return true;
    return false;
  }
}
